import React from 'react'
import cn from 'classnames'
import { Link } from 'react-router-dom'

import ArrowRight from '../assets/arrow-right.svg?svgr-webpack'
import StopWatch from '../assets/stopwatch.svg?svgr-webpack'
import Tag from '../assets/price-tag-outline.svg?svgr-webpack'
import MoreButton from './MoreButton'
import {
  LatestNutritionArticles_latestArticles,
  LatestNutritionArticles_start_featuredStory,
} from '../types/LatestNutritionArticles'
import { NonNullableObject } from 'typings'

type FeaturedStoryProps = {
  image: {
    url: string
  }
  desktopImage: { url: string }
  title: string
  excerpt: string
  categories: any[]
  className?: string
  slug: string
  content: any[]
}

function FeaturedStory({
  image,
  desktopImage,
  title,
  excerpt,
  categories,
  className,
  slug,
  content,
}: FeaturedStoryProps) {
  const words = content
    .map(({ text }) => text)
    .join()
    .split(' ')
  const minutes = words.length / 200
  const readingTime = Math.ceil(minutes)

  return (
    <section
      className={cn(
        'grid lg:grid-cols-2 lg:border border-Border lg:rounded-16 relative lg:shadow-elevated group',
        '-mx-20 lg:mx-0',
        className
      )}
    >
      <div className="bg-green px-20 pt-6 pb-4 absolute z-10 mt-24 lg:mt-32 rounded-r-full text-white uppercase type-upper-fat">
        Featured story
      </div>
      <div>
        {image && (
          <img
            srcSet={`${image.url} 375w, ${desktopImage.url} 640w`}
            sizes="(max-width: 999px) 375px, 640px"
            src={image.url}
            alt=""
            className="lg:rounded-l-16 w-full mb-16 lg:mb-0"
          />
        )}
      </div>
      <div className="mb-12 lg:my-24 mx-20 lg:mx-40 flex flex-col items-center justify-center text-center">
        <h1 className="text-green type-med-plus-fat xl:type-32-bold leading-default mb-12">
          <span className="transition border-b-3 xl:border-b-4 border-transparent group-hover:text-Type group-hover:border-green">
            {title}
          </span>
        </h1>
        <div className="text-grey type-14-caps mb-12 flex">
          <span className="mx-8 flex items-center">
            <StopWatch className="mr-6" />
            {`${readingTime} min. read`}
          </span>
          <span className="mx-8 flex items-center">
            <Tag className="mr-6" />
            {categories[0].name}
          </span>
        </div>
        <div className="leading-body mb-36">{excerpt}</div>
        <MoreButton
          className="fill-link"
          use={Link}
          to={`/nutrition-explained/${slug}`}
        >
          Read more<span className="sr-only">about {title}</span>
        </MoreButton>
      </div>
    </section>
  )
}

type ExcerptProps = {
  content: string
}

function Excerpt({ content }: ExcerptProps) {
  const wordsArray = content.split(' ')
  const last = wordsArray.pop()

  return (
    <>
      {wordsArray.join(' ')}
      <span className="whitespace-nowrap" key="last">
        {` ${last}`}
        <span
          className={`
              h-24 w-24
              inline-flex ml-6
              transition transform origin-left lg:scale-125
              group-hover:translate-x-1
              text-green group-hover:text-white
              bg-transparent group-hover:bg-green rounded-full
              group-hover:shadow-elevated
            `}
        >
          <ArrowRight className="m-auto" />
        </span>
      </span>
    </>
  )
}

type StoryCardProps = {
  image: { url: string } | null
  title: string
  className?: string
  slug: string
}

function StoryCard({ title, image, slug }: StoryCardProps) {
  return (
    <div className="last:hidden lg:last:block">
      <Link to={`/nutrition-explained/${slug}`} className="group">
        <div className="flex lg:flex-col items-center lg:items-start grow">
          <div
            className={cn(
              'shrink-0 w-2/5 lg:w-full',
              'rounded-6 lg:rounded-8',
              'mb-auto overflow-hidden'
            )}
          >
            {image && <img src={image.url} alt="" width="240" height="160" />}
          </div>
          <p className="ml-16 lg:ml-0 lg:mt-8 text-left text-black">
            <span
              className="leading-body"
              style={{ width: 'calc(100% - 20px)' }}
            >
              <Excerpt content={title} />
            </span>
          </p>
        </div>
      </Link>
    </div>
  )
}

type Props = {
  featuredStory: NonNullableObject<LatestNutritionArticles_start_featuredStory> | null
  latestArticles: NonNullableObject<LatestNutritionArticles_latestArticles>[]
}

export default function NutritionStartStories({
  featuredStory,
  latestArticles,
}: Props) {
  return (
    <div className="grid grid-col">
      <div className="lg:order-2 grid lg:grid-cols-4 gap-6 w-full mb-32">
        {latestArticles.map((story) => (
          <StoryCard key={story.id} {...story}></StoryCard>
        ))}
      </div>
      <MoreButton
        use={Link}
        to="/nutrition-explained/page/1/"
        className="lg:order-3 mx-auto"
      >
        See more
      </MoreButton>
      {featuredStory && (
        <FeaturedStory
          {...featuredStory}
          className="lg:order-1 mt-48 lg:mt-0 lg:mb-32"
        />
      )}
    </div>
  )
}
